import { type WaypointExtended, type WaypointOptionsJQuery } from './types';

/**
 * Extension of the jQuery Waypoint plugin.
 *
 * Waypoints are used to trigger behavior when an element is scrolled to. This function extends the default
 * waypoint functionality to allow for multiple waypoints to be created for a single element.
 *
 * @since ??
 *
 * @param {JQuery}  $element The element that you want to apply the waypoint to.
 * @param {object} options The options object that you would normally pass to the waypoint function.
 * @param {string|number} [options.offset] The offset option to pass to the waypoint function.
 * @param {boolean} [options.continuous] Whether or not the waypoint should be continuous.
 * @param {boolean} [options.enabled] Whether or not the waypoint should be enabled.
 * @param {string} [options.group] The group option to pass to the waypoint function.
 * @param {boolean} [options.horizontal] Whether or not the waypoint should be considered horizontal.
 * @param {Function} [options.handler] Custom handler function.
 * @param {HTMLElement|string} [options.context] Context to pass to the waypoint function.
 * @param {number} maxInstances The maximum number of waypoints that can be created for the element.
 *
 * @returns {void}
 */
const waypointExtended: WaypointExtended = ($element: JQuery, options: WaypointOptionsJQuery, maxInstances: number) => {
  maxInstances = parseInt(maxInstances?.toString(), 10);

  if (Number.isNaN(maxInstances)) {
    maxInstances = parseInt($element.data('et_waypoint_max_instances') as string, 10);
  }

  if (Number.isNaN(maxInstances)) {
    maxInstances = 1;
  }

  const currentInstances = $element.data('et_waypoint') || [];

  // Custom options.
  let contextSelectors: string[] = [];

  if ('et_pb_custom' in window) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore -- For TypeDoc: et_pb_custom is defined in the window object.
    contextSelectors = window?.et_pb_custom?.waypoints_options?.context;
  }

  if (contextSelectors && Array.isArray(contextSelectors)) {
    const $contextElement = contextSelectors.find(contextSelector => $element.closest(contextSelector).length > 0);

    if ($contextElement) {
      options.context = $contextElement;
    }
  }

  if (currentInstances.length < maxInstances) {
    const newInstances = $element.waypoint(options);

    if (newInstances && Array.isArray(newInstances) && newInstances.length > 0) {
      currentInstances.push(newInstances[0] as Waypoint);
      $element.data('et_waypoint', currentInstances);
    }
  } else {
    // Reinitialize existing
    currentInstances.forEach(instance => {
      instance.context.refresh();
    });
  }
};

export { waypointExtended };
